import { useRef, useState } from "react";
import { Sparkles, Video, Mail, Siren, Printer, Download, Mic, Square } from "lucide-react";
import { toast } from "sonner";
import { initials, avatarImgStyle } from "@/lib/profile";
import { PILLAR_LABELS } from "@/lib/instant";
import { adminGet, adminPost, adminPut, adminDel } from "@/lib/adminApi";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function esc(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function printHtml(detail, synthese) {
  const u = detail.user;
  const p = detail.parcours || {};
  const row = (label, value) => (value ? `<tr><td class="lbl">${esc(label)}</td><td>${esc(value)}</td></tr>` : "");
  const bars = (items, labelKey, pctKey) =>
    (items || [])
      .map((i) => `<tr><td class="lbl">${esc(PILLAR_LABELS[i[labelKey]] || i[labelKey])}</td><td>${pctKey ? i[pctKey] + " %" : i.count}</td></tr>`)
      .join("");
  const timeline = [
    ...(detail.instants || []).map((x) => ({ date: x.created_at, text: `Instant — ${x.feeling || x.need || "personnalisé"}${x.minutes ? ` · ${x.minutes} min` : ""}${x.feeling_after ? ` · après : ${x.feeling_after}` : ""}${x.stopped_at_pct != null ? ` · stoppée à ${x.stopped_at_pct} %` : ""}` })),
    ...(detail.rdv || []).map((r) => ({ date: r.created_at, text: `Rendez-vous — ${r.slot_label}${r.price != null ? ` · ${r.price} €` : ""} · ${r.status}` })),
    ...(detail.requests || []).map((r) => ({ date: r.created_at, text: `Demande — ${r.kind} · ${r.status}` })),
    ...(detail.sos_uses || []).map((x) => ({ date: x.created_at, text: `SOS — ${x.sos_name}` })),
  ].sort((a, b) => String(b.date).localeCompare(String(a.date)));

  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Fiche patientèle — ${esc([u.name, u.last_name].filter(Boolean).join(" ") || u.email)}</title>
<style>
  body { font-family: Georgia, serif; color: #4e3d31; margin: 32px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 15px; margin: 22px 0 6px; border-bottom: 1px solid #cfa067; padding-bottom: 4px; }
  .sub { color: #8a6a4f; font-size: 13px; margin: 0 0 8px; }
  table { border-collapse: collapse; width: 100%; }
  td { padding: 4px 8px; font-size: 13px; border-bottom: 1px solid #eee0cd; vertical-align: top; }
  .lbl { color: #8a6a4f; width: 220px; }
  .alert { background: #fdf3e3; border: 1px solid #cfa067; border-radius: 8px; padding: 6px 10px; margin: 4px 0; font-size: 13px; }
  .syn { font-style: italic; font-size: 14px; }
  .foot { margin-top: 28px; font-size: 11px; color: #b89a83; }
</style></head><body>
<h1>${esc([u.name, u.last_name].filter(Boolean).join(" ") || u.email)}</h1>
<p class="sub">Fiche patientèle 360° — Respire Aime Vibre · éditée le ${new Date().toLocaleDateString("fr-FR")}</p>
<h2>Identité</h2>
<table>
${row("E-mail", u.email)}${row("Naissance", u.birthdate)}${row("Âge", u.age != null ? `${u.age} ans` : "")}${row("Téléphone", u.phone_mobile)}${row("Adresse", [u.address_number, u.address_street, u.address_zip, u.address_city].filter(Boolean).join(" "))}${row("Compte créé le", String(u.created_at).slice(0, 10))}
</table>
${synthese ? `<h2>Synthèse 30 secondes</h2><p class="syn">${esc(synthese)}</p>` : ""}
${(p.alertes || []).length ? `<h2>À regarder</h2>${p.alertes.map((a) => `<div class="alert">${esc(a.text)}</div>`).join("")}` : ""}
<h2>Mon Instant</h2>
<table>
${row("Instants au total", p.instants_total)}${row("Sur 7 jours", p.instants_7j)}${row("Sur 30 jours", p.instants_30j)}${row("Durée préférée", p.duree_preferee ? `${p.duree_preferee} min` : "")}${row("Moment préféré", p.moment_prefere)}
</table>
${(p.pillars || []).length ? `<h2>Les cinq piliers</h2><table>${bars(p.pillars, "pilier", "pct")}</table>` : ""}
<h2>Rendez-vous</h2>
<table>
${row("Au total", p.rdv_total)}${row("Cette année", p.rdv_annee)}${row("Prochain", p.prochain_rdv?.slot_label)}${row("Dernier", p.dernier_rdv?.slot_label)}${row("Rythme", p.delai_moyen_rdv ? `1 tous les ${p.delai_moyen_rdv} jours` : "")}
</table>
<h2>SOS</h2>
<table>${row("Au total", p.sos_total)}${row("Sur 30 jours", p.sos_30j)}${row("Mois précédent", p.sos_mois_precedent)}</table>
${(p.feelings || []).length ? `<h2>Ressentis déclarés</h2><table>${bars(p.feelings, "ressenti")}</table>` : ""}
${(p.afters || []).length ? `<h2>Avant / Après l'Instant</h2><table>${bars(p.afters, "ressenti", "pct")}</table>` : ""}
${(p.evolution || []).length ? `<h2>Évolution du parcours</h2><table>${p.evolution.map((e) => `<tr><td class="lbl">${e.annee}</td><td>${e.instants} Instants · ${e.rendez_vous} rendez-vous · ${e.sos} SOS</td></tr>`).join("")}</table>` : ""}
<h2>Chronologie complète</h2>
<table>${timeline.map((t) => `<tr><td class="lbl">${esc(String(t.date).slice(0, 16).replace("T", " "))}</td><td>${esc(t.text)}</td></tr>`).join("")}</table>
<p class="foot">Document généré depuis l'administrateur Respire Aime Vibre — données personnelles, à traiter conformément au RGPD.</p>
</body></html>`;
}

export function frDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })} ${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`;
}

export function Avatar({ user, size = 46 }) {
  const style = { width: size, height: size };
  if (user.photo_url) {
    return <img className="rav-admin-avatar" style={{ ...style, ...avatarImgStyle(user) }} src={`${BACKEND}${user.photo_url}`} alt="" aria-hidden="true" />;
  }
  return (
    <span className="rav-admin-avatar rav-admin-avatar-initials" style={style} aria-hidden="true">
      {initials(user.name, user.last_name) || "·"}
    </span>
  );
}

function Bars({ items, labelKey, valueKey = "count", pctKey }) {
  const max = Math.max(1, ...items.map((i) => i[valueKey]));
  return (
    <div>
      {items.map((i) => (
        <div key={i[labelKey]} className="rav-bar-row">
          <span className="rav-bar-label">{PILLAR_LABELS[i[labelKey]] || i[labelKey]}</span>
          <span className="rav-bar-track">
            <span className="rav-bar-fill" style={{ width: `${(i[valueKey] / max) * 100}%` }} />
          </span>
          <span className="rav-bar-value">{pctKey ? `${i[pctKey]} %` : i[valueKey]}</span>
        </div>
      ))}
    </div>
  );
}

// Graphique analytique d'utilisation — par heure, jour, mois, trimestre ou année (calculé depuis l'historique existant)
const MONTHS_SHORT = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

function Chart12M({ instants = [], rdv = [], sos = [] }) {
  const [mode, setMode] = useState("mois");
  const [vue, setVue] = useState("colonnes"); // "colonnes" | "ligne"
  const now = new Date();
  const dateOf = (x, field = "created_at") => String(x[field] || x.date || "");

  let buckets = [];
  let titre = "";
  if (mode === "heure") {
    titre = "Utilisation par heure de la journée";
    for (let h = 0; h < 24; h++) {
      const key = ` ${String(h).padStart(2, "0")}`;
      buckets.push({
        key: `h${h}`,
        label: h % 3 === 0 ? `${h}h` : "",
        value: instants.filter((x) => dateOf(x).slice(11, 13) === String(h).padStart(2, "0")).length,
        extra: "",
      });
    }
  } else if (mode === "jour") {
    titre = "Utilisation par jour — 30 derniers jours";
    for (let k = 29; k >= 0; k--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - k);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      buckets.push({ key, label: k % 5 === 0 ? `${d.getDate()}/${d.getMonth() + 1}` : "", value: instants.filter((x) => dateOf(x).startsWith(key)).length, extra: "" });
    }
  } else if (mode === "trimestre") {
    titre = "Utilisation par trimestre — 8 derniers trimestres";
    for (let k = 7; k >= 0; k--) {
      const qMonth = now.getMonth() - k * 3;
      const d = new Date(now.getFullYear(), qMonth, 1);
      const q = Math.floor(d.getMonth() / 3) + 1;
      const start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const end = `${d.getFullYear()}-${String(d.getMonth() + 3).padStart(2, "0")}`;
      buckets.push({
        key: `${d.getFullYear()}-T${q}`,
        label: `T${q} ${String(d.getFullYear()).slice(2)}`,
        value: instants.filter((x) => dateOf(x).slice(0, 7) >= start && dateOf(x).slice(0, 7) <= end).length,
        extra: "",
      });
    }
  } else if (mode === "annee") {
    titre = "Utilisation par année";
    const years = [...new Set(instants.map((x) => dateOf(x).slice(0, 4)).filter(Boolean))].sort();
    const all = years.length ? years : [String(now.getFullYear())];
    all.forEach((y) => buckets.push({ key: y, label: y, value: instants.filter((x) => dateOf(x).startsWith(y)).length, extra: "" }));
  } else {
    titre = `Utilisation sur l'année ${now.getFullYear()}`;
    for (let m = 0; m < 12; m++) {
      const key = `${now.getFullYear()}-${String(m + 1).padStart(2, "0")}`;
      buckets.push({
        key,
        label: MONTHS_SHORT[m],
        value: instants.filter((x) => dateOf(x).startsWith(key)).length,
        extra: `${rdv.filter((x) => String(x.date || "").startsWith(key)).length} rendez-vous · ${sos.filter((x) => dateOf(x).startsWith(key)).length} SOS`,
      });
    }
  }

  const max = Math.max(1, ...buckets.map((b) => b.value));
  const total = buckets.reduce((s, b) => s + b.value, 0);
  return (
    <div className="rav-chart12" data-testid="chart-12m">
      <div className="rav-chart12-head">
        <h3 className="rav-admin-h3">{titre} — {total} Instant{total > 1 ? "s" : ""}</h3>
        <div className="rav-chart12-modes">
          {[["heure", "Heure"], ["jour", "Jour"], ["mois", "Mois"], ["trimestre", "Trimestre"], ["annee", "Année"]].map(([id, label]) => (
            <button key={id} type="button" className={`rav-chip rav-chart12-chip${mode === id ? " is-selected" : ""}`} data-testid={`chart-mode-${id}`} onClick={() => setMode(id)}>
              {label}
            </button>
          ))}
          <span className="rav-chart12-sep" />
          {[["colonnes", "Colonnes"], ["ligne", "Courbe"]].map(([id, label]) => (
            <button key={id} type="button" className={`rav-chip rav-chart12-chip${vue === id ? " is-selected" : ""}`} data-testid={`chart-vue-${id}`} onClick={() => setVue(id)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {vue === "ligne" ? (
        <div className="rav-chart12-line" data-testid="chart-line">
          <svg viewBox="0 0 100 42" preserveAspectRatio="none" aria-hidden="true">
            <polyline
              fill="none"
              stroke="var(--rav-gold-dark)"
              strokeWidth="1.6"
              strokeLinejoin="round"
              strokeLinecap="round"
              points={buckets.map((b, i) => `${buckets.length === 1 ? 50 : (i / (buckets.length - 1)) * 100},${40 - (b.value / max) * 34}`).join(" ")}
            />
            <polygon
              fill="rgba(207,160,103,0.18)"
              stroke="none"
              points={`0,42 ${buckets.map((b, i) => `${buckets.length === 1 ? 50 : (i / (buckets.length - 1)) * 100},${40 - (b.value / max) * 34}`).join(" ")} 100,42`}
            />
          </svg>
          <div className="rav-chart12-linelabels">
            {buckets.map((b) => (
              <span key={b.key} title={`${b.label || b.key} — ${b.value} Instants${b.extra ? ` · ${b.extra}` : ""}`}>{b.label}</span>
            ))}
          </div>
        </div>
      ) : (
      <div className="rav-chart12-cols">
        {buckets.map((b) => (
          <div key={b.key} className="rav-chart12-col" data-testid={`chart-col-${b.key}`} title={`${b.label || b.key} — ${b.value} Instants${b.extra ? ` · ${b.extra}` : ""}`}>
            <span className="rav-chart12-value">{b.value || ""}</span>
            <span className="rav-chart12-bar" style={{ height: `${Math.max(3, (b.value / max) * 100)}%` }} />
            <span className="rav-chart12-label">{b.label}</span>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

// Crédits d'Instants offerts personnalisés pour CE compte (prime sur le réglage global)
function CreditsEditor({ user }) {
  const [value, setValue] = useState(user.free_limit_override ?? "");
  const save = async () => {
    try {
      await adminPut(`/users/${user.id}/credits`, { free_limit_override: value === "" ? null : Math.max(0, parseInt(value, 10) || 0) });
      toast("Crédits offerts mis à jour pour ce compte.");
    } catch {
      toast("Une erreur est survenue.");
    }
  };
  return (
    <div className="rav-admin-credits" data-testid="credits-editor">
      <span className="rav-admin-row-sub">Instants offerts pour ce compte :</span>
      <input
        className="rav-input rav-admin-credits-input"
        data-testid="credits-override"
        type="number"
        min="0"
        placeholder="réglage global"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button type="button" className="rav-text-link" data-testid="credits-save" onClick={save}>
        Enregistrer
      </button>
    </div>
  );
}

// Notes privées de Julie — chaque note enregistrée devient une petite carte stockée sur la fiche
function NotesEditor({ user }) {
  const [cards, setCards] = useState(user.note_cards || []);
  const [text, setText] = useState(!user.note_cards?.length && user.admin_notes ? user.admin_notes : "");
  const [editingId, setEditingId] = useState(null);
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const micSupported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const toggleMic = () => {
    if (!micSupported) {
      toast("La dictée vocale n'est pas disponible sur ce navigateur (essayez Chrome).");
      return;
    }
    if (listening) {
      recogRef.current?.stop();
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "fr-FR";
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      let said = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) said += e.results[i][0].transcript;
      }
      if (said) setText((t) => (t ? t.trimEnd() + " " : "") + said.trim());
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => {
      setListening(false);
      toast("Micro indisponible ou non autorisé.");
    };
    recogRef.current = rec;
    rec.start();
    setListening(true);
    toast("Dictée en cours… parlez, le texte s'écrit tout seul.");
  };
  const sorted = [...cards].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  const fmtDate = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  };
  const save = async () => {
    if (!text.trim()) {
      toast("La note est vide.");
      return;
    }
    try {
      if (editingId) {
        await adminPut(`/users/${user.id}/notes/${editingId}`, { text: text.trim() });
        setCards(cards.map((c) => (c.id === editingId ? { ...c, text: text.trim(), updated_at: new Date().toISOString() } : c)));
        toast("Note mise à jour.");
      } else {
        const res = await adminPost(`/users/${user.id}/notes`, { text: text.trim() });
        setCards([...cards, res.note]);
        toast("Note enregistrée sur la fiche.");
      }
      setText("");
      setEditingId(null);
    } catch {
      toast("Enregistrement impossible.");
    }
  };
  const openCard = (card) => {
    setEditingId(card.id);
    setText(card.text);
  };
  const cancel = () => {
    setEditingId(null);
    setText("");
  };
  const remove = async () => {
    if (!editingId || !window.confirm("Supprimer définitivement cette note ?")) return;
    try {
      await adminDel(`/users/${user.id}/notes/${editingId}`);
      setCards(cards.filter((c) => c.id !== editingId));
      setText("");
      setEditingId(null);
      toast("Note supprimée.");
    } catch {
      toast("Suppression impossible.");
    }
  };
  return (
    <div className="rav-notes" data-testid="fiche-notes">
      <div className="rav-notes-head">
        <h3 className="rav-admin-h3">Notes de Julie</h3>
        <span className="rav-notes-date" data-testid="fiche-notes-date">
          {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </span>
      </div>
      <textarea
        className="rav-input rav-notes-area"
        data-testid="fiche-notes-area"
        rows={6}
        placeholder="Ce qui s'est dit au téléphone, ce qu'elle traverse, ce qu'on se promet…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="rav-notes-actions">
        <button
          type="button"
          className={`rav-note-mic${listening ? " is-listening" : ""}`}
          data-testid="fiche-notes-mic"
          onClick={toggleMic}
          title={listening ? "Arrêter la dictée" : "Dicter la note à voix haute"}
        >
          {listening ? <Square size={15} strokeWidth={1.8} /> : <Mic size={15} strokeWidth={1.8} />}
          {listening ? "Arrêter la dictée" : "Dicter"}
        </button>
        <button type="button" className="rav-text-link" data-testid="fiche-notes-save" onClick={save}>
          {editingId ? "Enregistrer la modification" : "Enregistrer la note"}
        </button>
        {editingId && (
          <>
            <button type="button" className="rav-text-link rav-note-delete" data-testid="fiche-notes-delete" onClick={remove}>
              Supprimer cette note
            </button>
            <button type="button" className="rav-text-link" data-testid="fiche-notes-cancel" onClick={cancel}>
              Nouvelle note
            </button>
          </>
        )}
      </div>
      {sorted.length > 0 && (
        <div className="rav-notes-grid" data-testid="fiche-notes-grid">
          {sorted.map((card) => (
            <button
              key={card.id}
              type="button"
              className={`rav-note-card${card.id === editingId ? " is-open" : ""}`}
              data-testid={`fiche-note-card-${card.id}`}
              onClick={() => openCard(card)}
              title="Cliquer pour rouvrir et modifier cette note"
            >
              <span className="rav-note-card-text">{card.text}</span>
              <span className="rav-note-card-date">{fmtDate(card.updated_at || card.created_at)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Fiche Patientèle 360° : le bloc identité existant est INTACT — la couche Parcours RAV est ajoutée en dessous.
export default function ClientFiche({ detail, booking, onClose }) {
  const [editing, setEditing] = useState(false);
  const [liveUser, setLiveUser] = useState(null);
  const [form, setForm] = useState(null);
  if (!detail && booking) {
    return (
      <div className="rav-admin-row rav-admin-detail" data-testid="client-fiche">
        <span className="rav-admin-row-label">{booking.name || "Cliente locale"}</span>
        <span className="rav-admin-row-sub">{[booking.email, booking.reason].filter(Boolean).join(" · ") || ""}</span>
        <span className="rav-admin-row-sub">
          Rendez-vous : {booking.slot_label}
          {booking.price != null ? ` · ${booking.price} €` : ""} · {booking.status}
        </span>
        <span className="rav-admin-row-sub">Cette personne n'a pas de compte sur l'application — clientèle locale.</span>
        <button type="button" className="rav-text-link" data-testid="client-fiche-close" onClick={onClose}>
          Fermer la fiche
        </button>
      </div>
    );
  }
  if (!detail) return null;

  const u = liveUser || detail.user;
  const s = detail.summary;
  const p = detail.parcours;

  const startEdit = () => {
    setForm({
      name: u.name || "",
      last_name: u.last_name || "",
      birthdate: u.birthdate || "",
      gender: u.gender || "",
      phone_country: u.phone_country || "+32",
      phone_mobile: u.phone_mobile || "",
      address_number: u.address_number || "",
      address_street: u.address_street || "",
      address_zip: u.address_zip || "",
      address_city: u.address_city || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const { user: updated } = await adminPut(`/users/${u.id}`, form);
      setLiveUser(updated);
      setEditing(false);
      toast("Fiche enregistrée.");
    } catch (err) {
      toast(err.response?.data?.detail || "Enregistrement impossible.");
    }
  };

  const timeline = [
    ...(detail.instants || []).map((x) => ({
      type: "instant",
      date: x.created_at,
      tag: x.pillar ? PILLAR_LABELS[x.pillar] || x.pillar : "Personnalisé",
      text: `Instant — ${x.feeling || x.need || "personnalisé"}${x.minutes ? ` · ${x.minutes} min` : ""}${x.feeling_after ? ` · après : ${x.feeling_after}` : ""}${x.stopped_at_pct != null ? ` · stoppée à ${x.stopped_at_pct} %` : ""}`,
    })),
    ...(detail.rdv || []).map((r) => ({
      type: "rdv",
      date: r.created_at,
      tag: "Visio",
      text: `Rendez-vous — ${r.slot_label}${r.price != null ? ` · ${r.price} €` : ""} · ${r.status}`,
    })),
    ...(detail.requests || []).map((r) => ({
      type: "demande",
      date: r.created_at,
      tag: r.kind || "Message",
      text: `Demande — ${r.status}${r.message ? ` — « ${r.message} »` : ""}`,
    })),
    ...(detail.sos_uses || []).map((x) => ({
      type: "sos",
      date: x.created_at,
      tag: "SOS",
      text: `SOS — ${x.sos_name}`,
    })),
  ].sort((a, b) => String(b.date).localeCompare(String(a.date)));

  const synthese = p
    ? [
        `Cliente depuis le ${p.cliente_depuis || "—"}.`,
        `${p.instants_total} Instant${p.instants_total > 1 ? "s" : ""} réalisé${p.instants_total > 1 ? "s" : ""}${p.moment_prefere ? `, surtout ${p.moment_prefere}` : ""}${p.duree_preferee ? `, durée préférée ${p.duree_preferee} min` : ""}.`,
        `${p.rdv_total} rendez-vous${p.prochain_rdv ? ` — prochain : ${p.prochain_rdv.slot_label}` : ""}${p.dernier_rdv ? ` — dernier : ${p.dernier_rdv.slot_label}` : ""}.`,
        p.pillars.length ? `Piliers favoris : ${p.pillars.slice(0, 2).map((x) => PILLAR_LABELS[x.pilier] || x.pilier).join(" et ")}.` : "",
        p.sos_30j ? `${p.sos_30j} SOS sur les 30 derniers jours.` : "",
      ]
        .filter(Boolean)
        .join(" ")
    : "";

  const printFiche = () => {
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) {
      toast("Autorisez les fenêtres pop-up pour imprimer la fiche.");
      return;
    }
    w.document.write(printHtml(detail, synthese));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const downloadFiche = async () => {
    try {
      const data = await adminGet(`/users/${u.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fiche-patientele-${(u.name || "compte").toLowerCase()}-${(u.last_name || "").toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast("Fiche téléchargée (données complètes).");
    } catch {
      toast("Téléchargement impossible.");
    }
  };

  // Activer / retirer le statut Super Administratrice d'un compte, depuis sa fiche
  const toggleRole = async () => {
    const makeAdmin = u.role !== "superadmin";
    const ok = window.confirm(
      makeAdmin
        ? `Donner les droits Super Administratrice à ${u.name || u.email} ?\n\nElle aura accès à tout l'administrateur, avec le bouton Admin dans son application.`
        : `Retirer les droits Super Administratrice à ${u.name || u.email} ?`,
    );
    if (!ok) return;
    try {
      await adminPut(`/users/${u.id}/role`, { role: makeAdmin ? "superadmin" : "user" });
      setLiveUser({ ...u, role: makeAdmin ? "superadmin" : "user" });
      toast(
        makeAdmin
          ? "Super Administratrice activée — le bouton Admin apparaît dans son application."
          : "Statut Super Administratrice retiré.",
      );
    } catch (err) {
      toast(err.response?.data?.detail || "Impossible de modifier le statut.");
    }
  };

  return (
    <div className="rav-admin-row rav-admin-detail" data-testid="client-fiche">
      <h2 className="rav-fiche-title" data-testid="fiche-title">Fiche patient</h2>
      <div className="rav-admin-fiche-actions">
        <button type="button" className="rav-text-link" data-testid="fiche-edit" onClick={startEdit}>
          Modifier les informations
        </button>
        <button type="button" className="rav-text-link" data-testid="fiche-print" onClick={printFiche}>
          <Printer size={15} strokeWidth={1.6} /> Imprimer la fiche
        </button>
        <button type="button" className="rav-text-link" data-testid="fiche-download" onClick={downloadFiche}>
          <Download size={15} strokeWidth={1.6} /> Télécharger les données
        </button>
        <button type="button" className="rav-text-link rav-note-delete" data-testid="fiche-toggle-admin" onClick={toggleRole}>
          {u.role === "superadmin" ? "Retirer le statut Super Admin" : "Activer le compte Super Admin"}
        </button>
      </div>
      {/* ===== BLOC IDENTITÉ EXISTANT — INTACT (édition possible via « Modifier les informations ») ===== */}
      <div className="rav-admin-user-row">
        <Avatar user={u} size={56} />
        <span className="rav-admin-user-texts">
          <span className="rav-admin-row-label">{[u.name, u.last_name].filter(Boolean).join(" ") || u.email}</span>
          <span className="rav-admin-row-sub">
            {u.email} · rôle : {u.role} · compte créé le {String(u.created_at).slice(0, 10)}
          </span>
          <span className="rav-admin-row-sub" data-testid="client-fiche-coords">
            {[
              u.birthdate ? `née le ${u.birthdate}` : null,
              u.age != null ? `${u.age} ans` : null,
              u.gender || null,
              u.phone_mobile ? `portable ${u.phone_country || ""} ${u.phone_mobile}`.trim() : null,
              [u.address_number, u.address_street, u.address_zip, u.address_city].filter(Boolean).join(" ") || null,
            ]
              .filter(Boolean)
              .join(" · ") || "Coordonnées à compléter"}
          </span>
        </span>
      </div>

      {/* Source d'acquisition (module Partenaires & Apporteurs) — lecture seule, sans recréer la fiche */}
      {detail.partner_acquisition && (
        <div className="rav-limit-card" data-testid="client-fiche-acquisition" style={{ margin: "10px 0" }}>
          <p style={{ margin: 0 }}>
            <strong>Source d'acquisition :</strong> Partenaire <strong>{detail.partner_acquisition.partner_name}</strong> ({detail.partner_acquisition.partner_pid})
            {detail.partner_acquisition.channel ? ` · Canal : ${detail.partner_acquisition.channel}` : ""}
            {detail.partner_acquisition.campaign_name ? ` · Campagne : ${detail.partner_acquisition.campaign_name}` : ""}
            {detail.partner_acquisition.link_name ? ` · Lien : ${detail.partner_acquisition.link_name}` : ""}
            {detail.partner_acquisition.method === "code" ? " · Via code partenaire" : ""}
            {detail.partner_acquisition.attributed_at ? ` · attribuée le ${String(detail.partner_acquisition.attributed_at).slice(0, 10).split("-").reverse().join("/")}` : ""}
          </p>
        </div>
      )}

      {editing && form && (
        <div className="rav-admin-form rav-admin-edit-form" data-testid="fiche-edit-form">
          <div className="rav-admin-form-row">
            <input className="rav-input" data-testid="edit-name" placeholder="Prénom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className="rav-input" data-testid="edit-lastname" placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <div className="rav-admin-form-row">
            <input className="rav-input" data-testid="edit-birthdate" type="date" value={form.birthdate} onChange={(e) => setForm({ ...form, birthdate: e.target.value })} />
            <select className="rav-input rav-admin-select" data-testid="edit-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
              <option value="">Femme ou homme…</option>
              <option value="fille">Une femme</option>
              <option value="garcon">Un homme</option>
            </select>
          </div>
          <div className="rav-admin-form-row">
            <input className="rav-input rav-admin-credits-input" data-testid="edit-phone-country" placeholder="+32" value={form.phone_country} onChange={(e) => setForm({ ...form, phone_country: e.target.value })} />
            <input className="rav-input" data-testid="edit-phone" placeholder="Portable (ex. 470 12 34 56)" value={form.phone_mobile} onChange={(e) => setForm({ ...form, phone_mobile: e.target.value })} />
          </div>
          <div className="rav-admin-form-row">
            <input className="rav-input rav-admin-credits-input" data-testid="edit-address-number" placeholder="N°" value={form.address_number} onChange={(e) => setForm({ ...form, address_number: e.target.value })} />
            <input className="rav-input" data-testid="edit-address-street" placeholder="Rue" value={form.address_street} onChange={(e) => setForm({ ...form, address_street: e.target.value })} />
          </div>
          <div className="rav-admin-form-row">
            <input className="rav-input rav-admin-credits-input" data-testid="edit-address-zip" placeholder="Code postal" value={form.address_zip} onChange={(e) => setForm({ ...form, address_zip: e.target.value })} />
            <input className="rav-input" data-testid="edit-address-city" placeholder="Ville" value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} />
          </div>
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="edit-save" onClick={saveEdit}>
              Enregistrer
            </button>
            <button type="button" className="rav-text-link" data-testid="edit-cancel" onClick={() => setEditing(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {s && (
        <div className="rav-admin-summary" data-testid="user-summary">
          <span><strong>{s.instants_count}</strong> Instants</span>
          <span><strong>{s.minutes_total}</strong> min pratiquées</span>
          <span><strong>{s.rdv_count}</strong> rendez-vous</span>
          <span><strong>{s.requests_count}</strong> demandes</span>
          {s.top_feeling && <span>Ressenti fréquent : <strong>{s.top_feeling}</strong></span>}
          {s.top_pillar && <span>Pilier favori : <strong>{PILLAR_LABELS[s.top_pillar] || s.top_pillar}</strong></span>}
          {s.last_activity && <span>Dernière activité : <strong>{frDateTime(s.last_activity)}</strong></span>}
        </div>
      )}

      <Chart12M instants={detail.instants} rdv={detail.rdv} sos={detail.sos_uses} />

      <CreditsEditor user={u} />

      {/* ===== PARCOURS RESPIRE AIME VIBRE — couche 360° calculée depuis les données existantes ===== */}
      {p && (
        <div data-testid="parcours-360">
          <h3 className="rav-admin-h3">Parcours Respire Aime Vibre — synthèse 30 secondes</h3>
          <p className="rav-admin-synthese" data-testid="parcours-synthese">{synthese}</p>

          {p.alertes.length > 0 && (
            <>
              <h3 className="rav-admin-h3">Que faut-il regarder ?</h3>
              <div className="rav-admin-alerts">
                {p.alertes.map((a, i) => (
                  <span key={i} className={`rav-admin-alert is-${a.level}`} data-testid={`parcours-alerte-${i}`}>
                    {a.text}
                  </span>
                ))}
              </div>
            </>
          )}

          <h3 className="rav-admin-h3">Mon Instant</h3>
          <div className="rav-admin-summary">
            <span><strong>{p.instants_total}</strong> au total</span>
            <span><strong>{p.instants_7j}</strong> sur 7 jours</span>
            <span><strong>{p.instants_30j}</strong> sur 30 jours</span>
            {p.duree_preferee && <span>Durée préférée : <strong>{p.duree_preferee} min</strong></span>}
            {p.moment_prefere && <span>Moment préféré : <strong>{p.moment_prefere}</strong></span>}
          </div>

          {p.pillars.length > 0 && (
            <>
              <h3 className="rav-admin-h3">Les cinq piliers — répartition</h3>
              <Bars items={p.pillars} labelKey="pilier" pctKey="pct" />
            </>
          )}

          <h3 className="rav-admin-h3">Rendez-vous</h3>
          <div className="rav-admin-summary">
            <span><strong>{p.rdv_total}</strong> au total</span>
            <span><strong>{p.rdv_annee}</strong> cette année</span>
            {p.rdv_annules > 0 && <span><strong>{p.rdv_annules}</strong> annulé(s)</span>}
            {p.delai_moyen_rdv && <span>Rythme : <strong>1 tous les {p.delai_moyen_rdv} jours</strong></span>}
            {p.prochain_rdv && <span>Prochain : <strong>{p.prochain_rdv.slot_label}</strong></span>}
            {p.prochain_rdv?.id && (
              <span>
                <button type="button" className="rav-text-link" data-testid="fiche-open-visio" onClick={() => window.open(`/visio/${p.prochain_rdv.id}`, "_blank")}>
                  Ouvrir la visio du prochain rendez-vous
                </button>
              </span>
            )}
            {p.dernier_rdv && <span>Dernier : <strong>{p.dernier_rdv.slot_label}</strong></span>}
          </div>

          {p.depuis_dernier_rdv?.date && (
            <>
              <h3 className="rav-admin-h3">Depuis le dernier rendez-vous ({p.depuis_dernier_rdv.date})</h3>
              <div className="rav-admin-summary">
                <span><strong>{p.depuis_dernier_rdv.instants}</strong> Instants</span>
                <span><strong>{p.depuis_dernier_rdv.sos}</strong> SOS</span>
              </div>
            </>
          )}

          <h3 className="rav-admin-h3">SOS</h3>
          <div className="rav-admin-summary">
            <span><strong>{p.sos_total}</strong> au total</span>
            <span><strong>{p.sos_30j}</strong> sur 30 jours</span>
            <span><strong>{p.sos_mois_precedent}</strong> le mois précédent</span>
            {p.sos_30j > p.sos_mois_precedent && p.sos_30j > 0 && <span><strong>En hausse</strong> par rapport au mois précédent</span>}
          </div>
          {p.sos_types.length > 0 && <Bars items={p.sos_types} labelKey="type" />}

          {p.feelings.length > 0 && (
            <>
              <h3 className="rav-admin-h3">Ressentis déclarés</h3>
              <Bars items={p.feelings} labelKey="ressenti" />
            </>
          )}

          {p.afters.length > 0 && (
            <>
              <h3 className="rav-admin-h3">Avant / Après l'Instant</h3>
              <Bars items={p.afters} labelKey="ressenti" pctKey="pct" />
            </>
          )}

          {p.evolution.length > 0 && (
            <>
              <h3 className="rav-admin-h3">Évolution du parcours</h3>
              <div className="rav-admin-summary">
                {p.evolution.map((e) => (
                  <span key={e.annee} data-testid={`parcours-annee-${e.annee}`}>
                    <strong>{e.annee}</strong> — {e.instants} Instants · {e.rendez_vous} rendez-vous · {e.sos} SOS
                  </span>
                ))}
              </div>
            </>
          )}

          {p.suggestion_pilier && (
            <>
              <h3 className="rav-admin-h3">Programme à envisager</h3>
              <p className="rav-admin-synthese" data-testid="parcours-suggestion">
                Suggestion : un contenu du pilier <strong>{PILLAR_LABELS[p.suggestion_pilier] || p.suggestion_pilier}</strong> — le moins exploré de son parcours.
              </p>
            </>
          )}
        </div>
      )}

      <h3 className="rav-admin-h3">Parcours complet — chronologie</h3>
      {timeline.length === 0 ? (
        <span className="rav-admin-row-sub">Aucune activité pour le moment.</span>
      ) : (
        <div className="rav-admin-timeline">
          {timeline.map((t, i) => (
            <div key={i} className="rav-admin-timeline-row" data-testid={`user-timeline-${i}`}>
              <span className={`rav-admin-timeline-icon is-${t.type}`}>
                {t.type === "instant" ? <Sparkles size={14} strokeWidth={1.6} /> : t.type === "rdv" ? <Video size={14} strokeWidth={1.6} /> : t.type === "sos" ? <Siren size={14} strokeWidth={1.6} /> : <Mail size={14} strokeWidth={1.6} />}
              </span>
              <span className="rav-admin-timeline-date">{frDateTime(t.date)}</span>
              <span className="rav-admin-row-sub">{t.text}</span>
              <span className="rav-admin-timeline-tag">{t.tag}</span>
            </div>
          ))}
        </div>
      )}

      <NotesEditor user={u} />

      <button type="button" className="rav-text-link" data-testid="client-fiche-close" onClick={onClose}>
        Fermer la fiche
      </button>
    </div>
  );
}
